/** TMA API 客户端：默认同域 /api/v1（nginx 反代到 Go，无 /backend 前缀） */

const BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");

const TOKEN_KEY = "tma_user_token";
const REFRESH_KEY = "tma_refresh_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setTokens(access: string, refresh?: string) {
  localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export type ApiEnvelope<T> = {
  code: number;
  message: string;
  data: T;
  trace_id?: string;
};

export class ApiError extends Error {
  constructor(
    public http: number,
    public bizCode: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function parse<T>(res: Response): Promise<T> {
  const json = (await res.json()) as ApiEnvelope<T>;
  if (!res.ok || json.code !== 0) {
    throw new ApiError(res.status, json.code ?? res.status, json.message || res.statusText);
  }
  return json.data;
}

export async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  headers.set("X-Client-Channel", "tma");
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  if (init.auth !== false) {
    const tok = getToken();
    if (tok) headers.set("Authorization", `Bearer ${tok}`);
  }
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  // 401 尝试 refresh
  if (res.status === 401 && init.auth !== false) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      const h2 = new Headers(headers);
      h2.set("Authorization", `Bearer ${getToken()}`);
      const res2 = await fetch(`${BASE}${path}`, { ...init, headers: h2 });
      return parse<T>(res2);
    }
  }
  return parse<T>(res);
}

async function tryRefresh(): Promise<boolean> {
  const rt = localStorage.getItem(REFRESH_KEY);
  if (!rt) return false;
  try {
    const res = await fetch(`${BASE}/api/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Client-Channel": "tma" },
      body: JSON.stringify({ refresh_token: rt }),
    });
    const json = (await res.json()) as ApiEnvelope<{
      access_token?: string;
      refresh_token?: string;
      token?: { access_token: string; refresh_token?: string };
    }>;
    if (json.code !== 0) {
      clearTokens();
      return false;
    }
    const d = json.data as any;
    const access = d.access_token || d.token?.access_token;
    const refresh = d.refresh_token || d.token?.refresh_token;
    if (!access) {
      clearTokens();
      return false;
    }
    setTokens(access, refresh);
    return true;
  } catch {
    clearTokens();
    return false;
  }
}

// ---- Auth ----
export async function loginTelegram(initData: string) {
  const data = await request<{
    user: UserVO;
    token: { access_token: string; refresh_token: string; expires_in: number };
  }>("/api/v1/auth/telegram", {
    method: "POST",
    auth: false,
    body: JSON.stringify({ init_data: initData }),
  });
  setTokens(data.token.access_token, data.token.refresh_token);
  return data;
}

export type UserVO = {
  id: number;
  email: string;
  locale?: string;
};

export async function patchMe(locale: string) {
  return request<UserVO>("/api/v1/me", {
    method: "PATCH",
    body: JSON.stringify({ locale }),
  });
}

export async function getMe() {
  return request<UserVO & { user?: UserVO }>("/api/v1/me");
}

// ---- Catalog ----
export type Product = {
  id: number;
  sku?: string;
  slug: string;
  name?: string;
  title?: string;
  retail_price_cents: number;
  currency?: string;
  /** 后端权威地理 */
  geo_type?: string;
  geo_codes?: string[];
  volume_bytes?: number;
  duration_value?: number;
  duration_unit?: string;
  data_display?: string;
  duration_display?: string;
  /** 兼容旧字段 */
  data_amount?: string;
  validity_days?: number;
  countries?: string[];
  cover_url?: string;
  status?: string;
};

export type CountryVO = {
  country_code: string;
  products: number;
  min_price_cents?: number;
  currency?: string;
};

export async function listCountries(params?: { q?: string; page?: number; size?: number }) {
  const sp = new URLSearchParams();
  if (params?.q) sp.set("q", params.q);
  if (params?.page) sp.set("page", String(params.page));
  if (params?.size) sp.set("size", String(params.size ?? 100));
  const qs = sp.toString();
  return request<{ list?: CountryVO[]; items?: CountryVO[]; total?: number } | CountryVO[]>(
    `/api/v1/catalog/countries${qs ? `?${qs}` : ""}`,
    { auth: false },
  );
}

/** C 端商品列表筛选 — 参数与后端 catalog.ListQuery 对齐 */
export type ProductListParams = {
  q?: string;
  country?: string;
  page?: number;
  size?: number;
  /** GB，含边界 */
  volume_min_gb?: number;
  volume_max_gb?: number;
  duration_min?: number;
  duration_max?: number;
  price_min_cents?: number;
  price_max_cents?: number;
  /** COUNTRY | REGION | GLOBAL */
  geo_type?: string;
  /** price | data | duration | recommend */
  sort?: string;
  order?: "asc" | "desc";
  facets?: boolean;
};

export type ProductFacets = {
  volumes?: { key: string; label: string; count: number }[];
  durations?: { key: string; label: string; count: number }[];
  geo_types?: { key: string; label: string; count: number }[];
  price_min_cents?: number;
  price_max_cents?: number;
};

export type ProductListResult = {
  list?: Product[];
  items?: Product[];
  total?: number;
  page?: number;
  size?: number;
  facets?: ProductFacets;
};

export async function listProducts(params?: ProductListParams) {
  const sp = new URLSearchParams();
  if (!params) {
    return request<ProductListResult | Product[]>(`/api/v1/catalog/products`, { auth: false });
  }
  const set = (k: string, v: string | number | boolean | undefined) => {
    if (v === undefined || v === "" || v === false) return;
    sp.set(k, String(v));
  };
  set("q", params.q);
  set("country", params.country);
  set("page", params.page);
  set("size", params.size ?? 50);
  set("volume_min_gb", params.volume_min_gb);
  set("volume_max_gb", params.volume_max_gb);
  set("duration_min", params.duration_min);
  set("duration_max", params.duration_max);
  set("price_min_cents", params.price_min_cents);
  set("price_max_cents", params.price_max_cents);
  set("geo_type", params.geo_type);
  set("sort", params.sort);
  set("order", params.order);
  if (params.facets) sp.set("facets", "1");
  const qs = sp.toString();
  return request<ProductListResult | Product[]>(
    `/api/v1/catalog/products${qs ? `?${qs}` : ""}`,
    { auth: false },
  );
}

/** 解析列表 total（筛选后数量） */
export function listTotal(data: unknown): number {
  if (Array.isArray(data)) return data.length;
  const d = data as ProductListResult;
  if (typeof d?.total === "number") return d.total;
  return asList(data).length;
}

export async function getProduct(slug: string) {
  return request<Product>(`/api/v1/catalog/products/${encodeURIComponent(slug)}`, { auth: false });
}

/** 列表 envelope 归一 */
export function asList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  const d = data as { items?: T[]; list?: T[]; products?: T[] };
  return d?.items || d?.list || d?.products || [];
}

/** 套餐数据量文案 */
export function productDataLabel(p: Product): string {
  if (p.data_display) return p.data_display.replace(/\.0\s/, " ");
  if (p.data_amount) return p.data_amount;
  const bytes = p.volume_bytes || 0;
  if (bytes <= 0) return "—";
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb >= 10 ? gb.toFixed(0) : gb.toFixed(1)} GB`;
  const mb = bytes / (1024 * 1024);
  return `${mb >= 10 ? mb.toFixed(0) : mb.toFixed(1)} MB`;
}

/** 套餐天数文案 */
export function productDurationLabel(p: Product, daysWord = "days"): string {
  if (p.duration_display) {
    return p.duration_display.replace(/DAY(S)?/i, daysWord).replace(/\s+/g, " ");
  }
  if (p.validity_days) return `${p.validity_days} ${daysWord}`;
  if (p.duration_value) {
    const u = (p.duration_unit || "DAY").toUpperCase();
    if (u.startsWith("DAY")) return `${p.duration_value} ${daysWord}`;
    return `${p.duration_value} ${p.duration_unit || ""}`.trim();
  }
  return "";
}

// ---- Orders / payments ----
export type OrderVO = {
  id: number;
  order_no: string;
  amount_cents: number;
  currency: string;
  status: string;
  /** 后端计算：是否可继续支付（CREATED） */
  can_pay?: boolean;
  /** 后端计算：是否可取消 */
  can_cancel?: boolean;
  product_title?: string;
  package_code?: string;
  product_id?: number;
  /** 商品地理（后端 EnrichView） */
  geo_type?: string;
  geo_codes?: string[];
  country_code?: string;
  created_at?: string;
  fail_reason?: string;
};

export async function cancelOrder(orderId: number) {
  return request<OrderVO>(`/api/v1/orders/${orderId}/cancel`, {
    method: "POST",
    body: "{}",
  });
}

export async function getOrder(orderId: number) {
  return request<{ order: OrderVO; esims?: unknown[]; can_pay?: boolean; can_cancel?: boolean }>(
    `/api/v1/orders/${orderId}`,
  );
}

export async function createOrder(productId: number, dryRun = false) {
  // 后端 CreateResult: { order, need_payment, ... }
  return request<{
    order: OrderVO;
    need_payment?: boolean;
    message?: string;
    dry_run?: boolean;
  }>("/api/v1/orders", {
    method: "POST",
    body: JSON.stringify({ product_id: productId, dry_run: dryRun, quantity: 1 }),
  });
}

export type CryptoAsset = {
  code: string;
  chain: string;
  symbol: string;
  network_label: string;
  decimals: number;
  rate_usd: number;
  wallets?: string[];
};

export async function listCryptoAssets() {
  return request<CryptoAsset[]>("/api/v1/crypto/assets", { auth: false });
}

export type CryptoCheckout = {
  mode: string;
  payment_no: string;
  asset_code: string;
  chain: string;
  symbol: string;
  network_label: string;
  amount_atomic: string;
  amount_display: string;
  receive_address: string;
  memo?: string;
  contract_address?: string;
  decimals: number;
  expires_at: string;
  status: string;
  tx_hash?: string;
  confirmations: number;
  required_confirmations: number;
  order_amount_cents: number;
  order_currency: string;
  wallets?: string[];
  ton?: {
    valid_until: number;
    to_address: string;
    amount_nanoton?: string;
    comment?: string;
    payload_b64?: string;
    is_jetton: boolean;
    jetton_master?: string;
    jetton_amount?: string;
    network: string;
  };
};

export function apiBase() {
  return (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");
}

export async function createCryptoPayment(orderId: number, assetCode: string) {
  return request<{
    payment: { payment_no: string; amount_cents: number; status: string };
    checkout: { mode: string; raw?: CryptoCheckout };
    crypto?: CryptoCheckout;
  }>("/api/v1/payments", {
    method: "POST",
    body: JSON.stringify({
      order_id: orderId,
      gateway: "crypto",
      asset_code: assetCode,
      channel: "tma",
    }),
  });
}

export async function getCryptoPayment(paymentNo: string) {
  return request<CryptoCheckout>(`/api/v1/crypto/payments/${encodeURIComponent(paymentNo)}`);
}

export async function submitCryptoTx(paymentNo: string, txHash: string, fromAddress?: string) {
  return request<CryptoCheckout>(`/api/v1/crypto/payments/${encodeURIComponent(paymentNo)}/tx`, {
    method: "POST",
    body: JSON.stringify({ tx_hash: txHash, from_address: fromAddress || "" }),
  });
}

export async function mockConfirmCrypto(paymentNo: string) {
  return request<CryptoCheckout>(
    `/api/v1/crypto/payments/${encodeURIComponent(paymentNo)}/mock-confirm`,
    { method: "POST", body: "{}" },
  );
}

export async function listMyOrders() {
  return request<{ items?: OrderVO[]; list?: OrderVO[] } | OrderVO[]>("/api/v1/orders");
}

export async function listMyEsims() {
  return request<{ items?: any[]; list?: any[] } | any[]>("/api/v1/esims");
}

export function centsUSD(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}
